import { FC, MouseEvent } from 'react';
import { SuggestItems } from '@doda/common/src/interfaces/suggestDispatcher';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import { IncludedKeywordCategoryRequestCode } from '@doda/api-client/aspida/@types';
import { KeywordSelectInput } from './components/KeywordSelectInput';

/**
 * KeywordSuggestコンポーネントのプロパティ
 */
interface Props {
  /** キーワード選択に関する状態 */
  state: {
    /** 「含む」フラグ。キーワードを含む検索かどうか */
    isInclude: boolean;
    /** 検索テキスト */
    searchText: string;
    /** サジェストリストアイテム */
    suggests: SuggestItems['items'];
    /** 検索カテゴリ */
    searchCategory: IncludedKeywordCategoryRequestCode | undefined;
  };
  /** キーワード選択に関する関数 */
  functions: {
    /** テキストが変更された時のハンドラー */
    onChangeText: (event: string) => void;
    /** サジェストアイテムがクリックされたときのハンドラー */
    onClickSuggest: (event: MouseEvent<HTMLButtonElement>) => void;
    /** キーワードカテゴリが更新された時のハンドラー */
    onChangeUpdateKeyword: (value: IncludedKeywordCategoryRequestCode) => void;
    /** インプットにフォーカスが当たった時のハンドラー */
    onFocusInput: () => void;
    /** サジェストリストをリセットする関数 */
    resetSuggests: () => void;
  };
  /** 求人件数を更新する関数 */
  updateJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
  /** オーバーレイの表示状態を変更するハンドラー（任意） */
  onChangeIsShowOverlay?: () => void;
}

/**
 * 検索サイトバー・キーワード
 * @param {Props} props - コンポーネントのプロパティ
 * @returns {JSX.Element} KeywordSuggestのJSX要素
 */
export const KeywordSuggest: FC<Props> = ({
  state,
  functions,
  updateJobCount,
  onChangeIsShowOverlay,
}) => {
  const { isInclude, searchText, suggests, searchCategory } = state;
  const {
    onChangeText,
    onClickSuggest,
    onChangeUpdateKeyword,
    onFocusInput,
    resetSuggests,
  } = functions;

  /**
   * カテゴリが変更された際の処理
   * @param {IncludedKeywordCategoryRequestCode} value - 新しいカテゴリ
   */
  const onChangeCategory = (value: IncludedKeywordCategoryRequestCode) => {
    onChangeUpdateKeyword(value);
    // 含むの場合かつキーワードが入力されている場合のみ求人件数取得APIを叩く
    if (isInclude && searchText) {
      updateJobCount({ includedKeywordCategory: value });
    }

    // 含まないの場合かつキーワードが入力されている場合のみ求人件数取得APIを叩く
    if (!isInclude && searchText) {
      updateJobCount({ excludedKeywordCategory: value });
    }

    // オーバーレイを表示
    if (onChangeIsShowOverlay) {
      onChangeIsShowOverlay();
    }
  };

  return (
    <KeywordSelectInput
      isInclude={isInclude}
      searchText={searchText}
      searchCategory={searchCategory}
      onChangeText={onChangeText}
      onChangeUpdateKeyword={onChangeCategory}
      onFocusInput={onFocusInput}
      onBlurInput={() => {
        updateJobCount();
        if (onChangeIsShowOverlay) {
          onChangeIsShowOverlay();
        }
      }}
      suggest={suggests}
      onClickSuggest={onClickSuggest}
      resetSuggests={resetSuggests}
    />
  );
};
